import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import jwtDecode, { JwtPayload } from "jwt-decode";
import moment from "moment";

type TokenListener = (token: string | null) => void;

const listeners: TokenListener[] = [];
let userToken = window.localStorage.getItem("userToken");

export interface DecodedUserToken extends JwtPayload {
  name?: string;
  unique_name?: string;
  role?: string | string[];
  escolaId?: string;
  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"?:
    | string
    | string[];
}

if (userToken) {
  const t = jwtDecode<JwtPayload>(userToken);
  if (t.exp && moment.unix(t.exp).isBefore()) {
    userToken = null;
  }
}

export function subscribeToUserToken(fn: TokenListener) {
  listeners.push(fn);
}

export function getUserToken(): string | null {
  return userToken;
}

export function getDecodedUserToken(): DecodedUserToken | null {
  if (!userToken) {
    return null;
  }

  try {
    return jwtDecode<DecodedUserToken>(userToken);
  } catch {
    return null;
  }
}

export function setUserToken(value: string | null) {
  userToken = value;
  if (value) {
    window.localStorage.setItem("userToken", value);
  } else {
    window.localStorage.removeItem("userToken");
  }
  for (const fn of listeners) {
    fn(value);
  }
}

export function getCurrentUser(): string | null {
  const decoded = getDecodedUserToken();
  return decoded?.name || decoded?.unique_name || null;
}

export function getCurrentUserRoles(): string[] {
  const decoded = getDecodedUserToken();

  if (!decoded) {
    return [];
  }

  const roles =
    decoded.role ||
    decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

  if (!roles) {
    return [];
  }

  return Array.isArray(roles) ? roles : [roles];
}

export function getCurrentEscolaId(): string | null {
  return getDecodedUserToken()?.escolaId || null;
}

export function isCurrentUserAdmin(): boolean {
  return getCurrentUserRoles().some(
    (role) => role.toLowerCase() === "admin"
  );
}

export interface PagedResponse<T> {
  data: T[];
}

export class BaseService {
  client: AxiosInstance;

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL: "/api/" + baseURL,
    });
  }

  _getConfig(params?: Record<string, unknown>): AxiosRequestConfig {
    const config: AxiosRequestConfig = { params };
    if (userToken) {
      config.headers = { Authorization: `Bearer ${userToken}` };
    }
    return config;
  }

  get<T>(url: string, params?: Record<string, unknown>) {
    return this.sendRequest(this.client.get<T>(url, this._getConfig(params)));
  }

  post<T, P = Record<string, unknown>>(
    url: string,
    data?: P,
    params?: Record<string, unknown>
  ) {
    return this.sendRequest(
      this.client.post<T>(url, data, this._getConfig(params))
    );
  }

  patch<T, P = Record<string, unknown>>(url: string, data?: P) {
    return this.sendRequest(this.client.patch<T>(url, data, this._getConfig()));
  }

  put<T, P = Record<string, unknown>>(url: string, data?: P) {
    return this.sendRequest(this.client.put<T>(url, data, this._getConfig()));
  }

  delete<T>(url: string) {
    return this.sendRequest(this.client.delete<T>(url, this._getConfig()));
  }

  download(url: string) {
    return this.sendRequest(
      this.client.get<Blob>(url, {
        ...this._getConfig(),
        responseType: "blob",
      })
    );
  }

  async sendRequest<R>(request: Promise<AxiosResponse<R>>) {
    try {
      const response = await request;
      return response.data;
    } catch (e) {
      if (e instanceof AxiosError && e.response) {
        const data = e.response.data;
        if (data) {
          if (data.errors) {
            for (const key in data.errors) {
              throw new Error(data.errors[key][0]);
            }
          }
          if (data.detail) {
            throw new Error(data.detail);
          } else if (data.title) {
            throw new Error(data.title);
          }
        }
      }
      throw e;
    }
  }
}
